/**
 * KeyboardShortcutsDialog
 *
 * Centralised reference for every keyboard shortcut in Otto, opened via
 * the `?` (Shift+/) global hotkey — the same convention GitHub, Linear,
 * Slack, and VS Code use.
 *
 * Every row is sourced from the central `keyboardShortcuts` registry so
 * adding a new shortcut means one entry there, not chasing scattered
 * docs. Keys render as platform-aware glyph clusters (⌘ on Mac, Ctrl
 * elsewhere).
 *
 * The `?` toggle is owned by `useKeyboardShortcutsHelp` in RootLayout —
 * the dialog itself does not re-listen for `?`. Two listeners on the
 * same key race the React state update; one is enough.
 */

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  getShortcut,
  getShortcutParts,
  getShortcutsByCategory,
  type ShortcutCategory,
  type ShortcutId,
} from '@/utils/input';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

// Section titles + ordered IDs to surface in the help dialog. Anything
// missing from these lists won't appear, which is intentional — some
// shortcuts (copy/cut/paste) are universal browser conventions and
// would be noise here.
const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  workflow: 'Workflow',
  view: 'View',
  edit: 'Edit',
  navigation: 'Navigation',
};

const CATEGORY_ORDER: ShortcutCategory[] = [
  'navigation',
  'workflow',
  'view',
  'edit',
];

const useStyles = makeStyles({
  surface: {
    // Slightly wider than the previous 560 to comfortably fit the
    // two-column shortcut layout below without breaking the keycap
    // clusters onto a second line on the narrower Edit/View rows.
    maxWidth: '640px',
    width: 'min(640px, 92vw)',
  },
  body: {
    // Multi-column layout: each `group` (Navigation, Workflow, View, …)
    // flows as a single chunk and `column-fill: balance` settles them
    // into two roughly equal columns. This roughly halves the dialog
    // height vs. the single-column layout, without sacrificing legibility
    // — the keycap clusters are short enough that a 640px surface gives
    // each column ~280px of usable width.
    columnCount: 2,
    columnGap: tokens.spacingHorizontalXXL,
    // Cap content area so the dialog can never push past comfortable
    // viewport height on small screens. Beyond this the content scrolls
    // inside; in practice with two columns the full registry already
    // fits well within this cap.
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    // Keep a category together when the column-balancer decides where
    // to break. Without this, "View" can end with two rows in column A
    // and three more in column B, which makes the page hard to scan.
    breakInside: 'avoid',
    // Space between consecutive groups within the same column. Using
    // `marginBlockStart` instead of a parent `gap` because columns
    // don't honour gap between flowed children.
    ':not(:first-child)': {
      marginBlockStart: tokens.spacingVerticalL,
    },
  },
  groupTitle: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalXXS,
  },
  rowLabel: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  keyGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  keycap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '22px',
    paddingInline: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    // Use the base UI font (not monospace) so Mac modifier glyphs
    // (⇧ ⌘ ⌥ ⌃ ⏎) render at a consistent size — most monospace fonts
    // substitute these from a fallback at a smaller size.
    fontFamily: tokens.fontFamilyBase,
    lineHeight: 1,
  },
  separator: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

interface RenderRow {
  id: ShortcutId;
  description: string;
  parts: readonly string[];
}

function buildRows(): { category: ShortcutCategory; rows: RenderRow[] }[] {
  const grouped = getShortcutsByCategory();
  return CATEGORY_ORDER.filter((cat) => grouped[cat].length > 0).map((cat) => ({
    category: cat,
    rows: grouped[cat].map((id) => ({
      id,
      description: getShortcut(id).description,
      parts: getShortcutParts(id),
    })),
  }));
}

export const KeyboardShortcutsDialog = ({
  open,
  onClose,
}: KeyboardShortcutsDialogProps) => {
  const styles = useStyles();
  const groups = buildRows();

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogContent className={styles.body}>
            {groups.map((group) => (
              <div key={group.category} className={styles.group}>
                <span className={styles.groupTitle}>
                  {CATEGORY_LABELS[group.category]}
                </span>
                {group.rows.map((row) => (
                  <div key={row.id} className={styles.row}>
                    <span className={styles.rowLabel}>{row.description}</span>
                    <span className={styles.keyGroup}>
                      {row.parts.map((part, i) => (
                        <span key={`${part}-${i}`} className={styles.keyGroup}>
                          {i > 0 && (
                            <span className={styles.separator}>+</span>
                          )}
                          <kbd className={styles.keycap}>{part}</kbd>
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
